// src/components/reportes/ReporteDoc.jsx
// npm i @react-pdf/renderer
import React from "react";
import { formattedGlobalDate } from "../../utils/formattedDates";
import {
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    Svg,
    Path,
} from "@react-pdf/renderer";

/* ============= A4 Layout ============= */
const PAGE_PAD_V = 56.7; // ~20mm
const PAGE_PAD_H = 39.7; // ~14mm
const CONTENT_W = 595 - 2 * PAGE_PAD_H;

const CMP_GAP = 12;
const CMP_CARD_W = (CONTENT_W - CMP_GAP) / 2;
const CMP_IMG_H = 180;

/* ============= Estilos ============= */
const styles = StyleSheet.create({
    page: {
        paddingTop: PAGE_PAD_V,
        paddingBottom: PAGE_PAD_V,
        paddingHorizontal: PAGE_PAD_H,
        backgroundColor: "#fff",
        color: "#000",
        fontFamily: "Helvetica",
        fontSize: 12,
        lineHeight: 1.4,
    },

    logo: { width: 180, height: 44, marginBottom: 14 },
    titleLabel: {
        fontSize: 16,
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 50,
    },

    row4: { flexDirection: "row", flexWrap: "wrap", marginBottom: 6, fontSize: 11 },
    col50: { width: "50%", marginBottom: 3, paddingRight: 8 },
    bold: { fontWeight: "bold" },

    mapsRow: { flexDirection: "row", marginTop: 20 },
    mapCol: { width: "50%", paddingRight: 10 },
    mapImg: { width: "100%", height: 300, borderWidth: 1, borderColor: "#000" },

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: "#000",
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tr: { flexDirection: "row" },
    th: {
        flex: 1,
        padding: 2,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        backgroundColor: "#eee",
        borderColor: "#000",
        fontSize: 10,
        fontWeight: "bold",
    },
    td: {
        flex: 1,
        padding: 2,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: "#000",
        fontSize: 10,
    },

    /* ===== Análisis de seguridad ===== */
    cmpPageTitle: { fontSize: 16, fontWeight: "bold", marginBottom: 12 },
    cmpItem: { marginBottom: 22 },
    cmpImgsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 14,
    },

    cmpCard: { width: CMP_CARD_W },
    cmpCardBox: {
        borderWidth: 1,
        borderColor: "#000",
        borderRadius: 4,
        backgroundColor: "#fff",
    },

    cmpBorderGreen: { borderColor: "green" },
    cmpBorderYellow: { borderColor: "gold" },
    cmpBorderRed: { borderColor: "red" },

    cmpImgWrap: { position: "relative" },
    cmpImg: {
        width: CMP_CARD_W,
        height: CMP_IMG_H,
        objectFit: "cover",
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#000",
    },

    /* Bounding boxes (sin etiquetas) */
    bbMain: { position: "absolute", borderWidth: 2, borderColor: "#0066ff" },
    bbRel: { position: "absolute", borderWidth: 2, borderColor: "#dc0000" },

    /* Info y resultados */
    cmpInfoBlock: { padding: 6, fontSize: 8 },
    cmpResults: { marginTop: 6, paddingHorizontal: 6 },
    textJustify: { textAlign: "justify" },
    cmpResultLine: { fontSize: 11, marginBottom: 4 },

    /* ====== Fotos 3×3 (compacto y exacto) ====== */
    gridRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginHorizontal: -3,
    },
    card: {
        width: "33.33%",
        padding: 3,
    },
    cardBox: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: "#000",
        backgroundColor: "#fff",
        minHeight: 200,
        flexDirection: "column",
    },
    cardImg: {
        width: "100%",
        height: 140,
        objectFit: "cover",
        borderBottomWidth: 1,
        borderColor: "#000",
    },
    cardBody: {
        paddingTop: 3,
        paddingBottom: 3,
        paddingHorizontal: 4,
        flexGrow: 0,            // evita estirar la caja
    },
    cardText: {
        fontSize: 8,
        lineHeight: 1.05,       // altura de línea compacta
    },
    metaBlock: {
        fontSize: 8,
        lineHeight: 1.05,
    }
});

const fmtCoord = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(6) : (v ?? "-");
};


/* ============= Iconos SVG (react-pdf) ============= */
function IconCheck() {
    return (
        <Svg width={10} height={10} viewBox="0 0 24 24">
            <Path
                fill="#10893E"
                d="M20.285 6.709l-11.61 11.61L3.714 13.36l1.413-1.414 3.548 3.547 10.196-10.195z"
            />
        </Svg>
    );
}
function IconWarning() {
    return (
        <Svg width={10} height={10} viewBox="0 0 24 24">
            <Path
                fill="#D0A300"
                d="M1 21h22L12 2 1 21zm12-3h-2v2h2v-2zm0-6h-2v4h2v-4z"
            />
        </Svg>
    );
}
function IconError() {
    return (
        <Svg width={10} height={10} viewBox="0 0 24 24">
            <Path
                fill="#C50F1F"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10
           10-4.48 10-10S17.52 2 12 2zm5 13l-1.41 1.41
           L12 13.41 8.41 16.41 7 15l4-4-4-4L8.41 5.59
           12 9.59l3.59-3.59L17 7l-4 4 4 4z"
            />
        </Svg>
    );
}

/* Helpers de icono para STATUS */
const iconByScore = (val) => {
    if (val === 1) return <IconCheck />;
    if (val === 2) return <IconWarning />;
    if (val >= 3) return <IconError />;
    return null;
};

/* ============= Helpers ============= */
const chunk = (a = [], s = 1) => {
    const r = [];
    for (let i = 0; i < a.length; i += s) r.push(a.slice(i, i + s));
    return r;
};
/* ============= Boxes SIN etiquetas ============= */
function BoxesOverImage({ ev }) {
    if (!ev) return null;

    const boxes = [];
    if (ev.bounding_box) boxes.push({ type: "main", ...ev.bounding_box });
    if (Array.isArray(ev.related_objects)) {
        ev.related_objects.forEach((r) =>
            boxes.push({ type: "rel", ...(r?.boundingBox || {}) })
        );
    }
    if (!boxes.length) return null;

    return (
        <>
            {boxes.map((b, i) => (
                <View
                    key={i}
                    style={[
                        (b.type || "").toLowerCase() === "rel" ? styles.bbRel : styles.bbMain,
                        {
                            left: Number(b.left || 0) * CMP_CARD_W,
                            top: Number(b.top || 0) * CMP_IMG_H,
                            width: Number(b.width || 0) * CMP_CARD_W,
                            height: Number(b.height || 0) * CMP_IMG_H,
                        },
                    ]}
                />
            ))}
        </>
    );
}

/* ============= Fila Contrato ============= */
const Row4 = ({ l1, v1, l2, v2 }) => (
    <View style={styles.row4}>
        <View style={styles.col50}>
            <Text>
                <Text style={styles.bold}>{l1} </Text>
                {v1 ?? ""}
            </Text>
        </View>
        <View style={styles.col50}>
            <Text>
                {l2 ? <Text style={styles.bold}>{l2} </Text> : null}
                {v2 ?? ""}
            </Text>
        </View>
    </View>
);

/* ============= Tabla Paginada ============= */
const TABLE_ROWS = 28;
function TablaPaginas({ rows = [] }) {
    if (!rows.length) return null;
    return chunk(rows, TABLE_ROWS).map((slice, idx) => (
        <Page key={idx} size="A4" style={styles.page}>
            <Text style={styles.bold}>Trazabilidad</Text>
            <View style={styles.table}>
                <View style={styles.tr}>
                    {[
                        "Fecha/Hora",
                        "Acción",
                        "Estado",
                        "Ubicación",
                        "Observaciones",
                        "Usuario",
                    ].map((h) => (
                        <Text key={h} style={styles.th}>
                            {h}
                        </Text>
                    ))}
                </View>
                {slice.map((r, i) => (
                    <View key={i} style={styles.tr}>
                        <Text style={styles.td}>{formattedGlobalDate(r?.XTime)}</Text>
                        <Text style={styles.td}>{r?.TipoAccion ?? ""}</Text>
                        <Text style={styles.td}>{r?.TipoReporte ?? ""}</Text>
                        <Text style={styles.td}>{r?.Ubicacion ?? ""}</Text>
                        <Text style={styles.td}>{r?.Nota ?? ""}</Text>
                        <Text style={styles.td}>{r?.XUser ?? ""}</Text>
                    </View>
                ))}
            </View>
        </Page>
    ));
}

/* ============= Comparación (STATUS con icono por score; ALERTAS con icono por flag) ============= */
/* ============= Comparación: EXACTAMENTE 2 por página (última puede tener 1) ============= */
function ComparisionSection({ comparision = [] }) {
    if (!comparision.length) return null;

    const MAX_PER_PAGE = 2;
    const MAX_IMGS_PER_ITEM = 2;

    const items = comparision.slice().reverse();
    const pages = [];
    for (let i = 0; i < items.length; i += MAX_PER_PAGE) {
        pages.push(items.slice(i, i + MAX_PER_PAGE));
    }

    return pages.map((slice, pageIdx) => (
        <Page key={pageIdx} size="A4" style={styles.page}>
            {pageIdx === 0 && (
                <Text style={styles.cmpPageTitle}>Análisis de seguridad</Text>
            )}

            {slice.map((item, idx) => {
                const border =
                    item?.scoreColor === "green" ? styles.cmpBorderGreen
                        : item?.scoreColor === "yellow" ? styles.cmpBorderYellow
                            : item?.scoreColor === "red" ? styles.cmpBorderRed
                                : null;

                const evsRaw = Array.isArray(item?.enrichedEvents) ? item.enrichedEvents : [];
                const evs = evsRaw.slice(0, MAX_IMGS_PER_ITEM); // evita overflow
                const res = item?.results || {};
                const alerts = res?.alerts || {};
                const scores = item?.scores || {};

                // Líneas (STATUS + ALERTAS + NOTAS)
                const lines = [];
                if (typeof res.status === "string" && res.status.length > 0) {
                    lines.push({
                        kind: "status",
                        icon: iconByScore(scores?.status),
                        bold: "STATUS:",
                        text: res.status,
                    });
                }
                const alertDefs = [
                    ["container_closure", "container_closure"],
                    ["physical_condition", "physical_condition"],
                    ["seals", "seals"],
                    ["security_labels", "security_labels"],
                    ["electronic_seal", "electronic_seal"],
                ];
                if (res?.alerts) {
                    alertDefs.forEach(([flagKey, msgKey]) => {
                        const flag = !!alerts[flagKey];
                        const message = res?.[msgKey];
                        if (typeof message === "string" && message.length > 0) {
                            lines.push({
                                kind: "alert",
                                icon: flag ? <IconWarning /> : <IconCheck />,
                                bold: null,
                                text: message,
                            });
                        }
                    });
                }
                if (Array.isArray(res?.notes)) {
                    res.notes.forEach((t) =>
                        lines.push({ kind: "note", icon: null, bold: null, text: String(t) })
                    );
                }

                return (
                    <View key={idx} style={[styles.cmpItem, { marginBottom: 18 }]} wrap={false}>
                        {/* Tarjetas de imágenes (máx 2) */}
                        <View style={[styles.cmpImgsRow, { marginBottom: 12 }]}>
                            {evs.map((ev, i) => {
                                const img = ev?.imageBase64 || ev?.imageUrl || "";
                                if (!img) return null;
                                return (
                                    <View key={i} style={styles.cmpCard}>
                                        <View style={[styles.cmpCardBox, border]}>
                                            <View style={styles.cmpImgWrap}>
                                                <Image src={img} style={styles.cmpImg} />
                                                <BoxesOverImage ev={ev} />
                                            </View>
                                            {(ev?.meta?.Evento || ev?.meta?.Hora) && (
                                                <View style={styles.cmpInfoBlock}>
                                                    <Text style={styles.textJustify}>
                                                        <Text style={styles.bold}>ID: </Text>
                                                        {item?.assetId ?? "-"}
                                                    </Text>
                                                    <Text style={styles.textJustify}>
                                                        <Text style={styles.bold}>Evento: </Text>
                                                        {ev?.meta?.Evento ?? "-"}
                                                    </Text>
                                                    <Text style={styles.textJustify}>
                                                        <Text style={styles.bold}>Fecha/Hora: </Text>
                                                        {ev?.meta?.Hora ?? "-"}
                                                    </Text>
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                );
                            })}
                        </View>

                        {/* Resultados */}
                        <View style={[styles.cmpResults, { marginTop: 4 }]}>
                            <Text style={[styles.bold, { marginBottom: 4 }]}>
                                {(item?.assetType || "Asset")} - {(item?.assetId || "-")}
                            </Text>

                            {lines.map((ln, j) => (
                                <View
                                    key={j}
                                    style={{ flexDirection: "row", alignItems: "center", marginBottom: 3 }}
                                >
                                    {ln.icon ? ln.icon : null}
                                    <Text
                                        style={[
                                            styles.cmpResultLine,
                                            styles.textJustify,
                                            { marginLeft: ln.icon ? 4 : 0 },
                                        ]}
                                    >
                                        {ln.bold ? <Text style={styles.bold}>{ln.bold} </Text> : null}
                                        {String(ln.text)}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </View>
                );
            })}
        </Page>
    ));
}



/* ============= Fotos 3x3 (grilla exacta por página) ============= */
function FotosPaginas({ fotos = [] }) {
    if (!fotos.length) return null;
    return chunk(fotos, 9).map((slice, idx) => (
        <Page key={idx} size="A4" style={styles.page}>
            <View style={styles.gridRow}>
                {slice.map((ph, i) => (
                    <View key={i} style={styles.card}>
                        <View style={styles.cardBox}>
                            {ph?.SourceBase64 || ph?.Source ? (
                                <Image src={ph.SourceBase64 || ph.Source} style={styles.cardImg} />
                            ) : null}
                            <View style={styles.cardBody}>
                                <Text style={styles.metaBlock}>
                                    {`Evento: ${ph?.Evento ?? "-"}`}
                                    {"\n"}{`Lat: ${fmtCoord(ph?.Latitud)}`}
                                    {"\n"}{`Lng: ${fmtCoord(ph?.Longitud)}`}
                                    {"\n"}{`Geocerca: ${ph?.Geo ?? "-"}`}
                                    {"\n"}{ph?.Hora ?? ""}
                                </Text>
                            </View>

                        </View>
                    </View>
                ))}
            </View>
        </Page>
    ));
}

/* ============= Documento Principal ============= */
export default function ReporteDoc({ data = {}, logoUrl }) {
    const contrato = data?.contrato || {};
    const mapas = data?.mapas || {};
    const reportes = Array.isArray(data?.reportes) ? data.reportes : [];
    const comparision = Array.isArray(data?.comparision) ? data.comparision : [];
    const fotos = Array.isArray(data?.fotos) ? data.fotos : [];

    return (
        <Document>
            {/* Página 1 */}
            <Page size="A4" style={styles.page}>
                {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
                <Text style={styles.titleLabel}>REPORTE DE SERVICIO EN TRÁNSITO</Text>

                <Row4
                    l1="CLIENTE:"
                    v1={contrato?.NombreEmpresa}
                    l2="COD. SERVICIO:"
                    v2={contrato?.ContractID}
                />
                <Row4
                    l1="RUTA:"
                    v1={contrato?.DescripcionRuta}
                    l2="DISPOSITIVO:"
                    v2={contrato?.FKLokDeviceID}
                />
                <Row4
                    l1="CONDUCTOR:"
                    v1={contrato?.NombreConductor}
                    l2="CELULAR:"
                    v2={contrato?.MovilConductor}
                />
                <Row4
                    l1="PLACA CABEZOTE:"
                    v1={contrato?.PlacaTruck}
                    l2="PLACA REMOLQUE:"
                    v2={contrato?.PlacaTrailer}
                />
                <Row4
                    l1="TRANSPORTADOR:"
                    v1={contrato?.NombreTranspo}
                    l2="INICIO SERVICIO:"
                    v2={contrato?.fecha}
                />
                <Row4
                    l1="CONTENEDOR N°:"
                    v1={contrato?.ContainerNum}
                    l2="FINAL SERVICIO:"
                    v2={contrato?.fechafin}
                />
                <Row4 l1="REFERENCIA:" v1={contrato?.Ref} />

                <View style={styles.mapsRow}>
                    <View style={styles.mapCol}>
                        <Text style={styles.bold}>Último Punto</Text>
                        {mapas?.ultimoPuntoUrl ? (
                            <Image src={mapas.ultimoPuntoUrl} style={styles.mapImg} />
                        ) : (
                            <Text style={{ fontSize: 10 }}>No disponible</Text>
                        )}
                    </View>
                    <View style={styles.mapCol}>
                        <Text style={styles.bold}>Recorrido</Text>
                        {mapas?.recorridoUrl ? (
                            <Image src={mapas.recorridoUrl} style={styles.mapImg} />
                        ) : (
                            <Text style={{ fontSize: 10 }}>No disponible</Text>
                        )}
                    </View>
                </View>
            </Page>

            <TablaPaginas rows={reportes} />
            <ComparisionSection comparision={comparision} />
            <FotosPaginas fotos={fotos} />
        </Document>
    );
}
